/**
 * Variant Card Component
 * Displays variant information in an improved card format
 */

import { useTranslation } from '@ufc/core'
import { Card, CardContent, CardHeader, CardTitle, Badge, ToggleGroup, ToggleGroupItem, cn } from '@ufc/uikit'
import { Box, Scale, Barcode, Loader2 } from 'lucide-react'
import type { Variant } from '../../domain/types'
import { getLocalizedName, formatWeight } from '../../domain/utils'
import { PRODUCT_STATUS_CONFIG, PACKAGING_CONFIG, PORTAL_AVAILABILITY_OPTIONS, PORTAL_AVAILABILITY_CONFIG } from '../../domain/constants'

function getStatusConfig(isActive: boolean) {
  return isActive ? PRODUCT_STATUS_CONFIG.active : PRODUCT_STATUS_CONFIG.inactive
}

function getPackagingConfig(packaging: 'bags' | 'bulk') {
  return PACKAGING_CONFIG[packaging]
}

interface VariantCardProps {
  variant: Variant
  isSelected?: boolean
  onSelect?: (variant: Variant) => void
  onEdit?: (variant: Variant) => void
  onDelete?: (variant: Variant) => void
  onPortalAvailabilityChange?: (variant: Variant, value: 'ecommerce' | 'portal' | 'both') => void
  isUpdatingPortalAvailability?: boolean
  locale?: 'en' | 'ar'
}

export function VariantCard({
  variant,
  isSelected = false,
  onSelect,
  onEdit: _onEdit,
  onDelete: _onDelete,
  onPortalAvailabilityChange,
  isUpdatingPortalAvailability = false,
  locale = 'en',
}: VariantCardProps) {
  const { t } = useTranslation()
  const statusConfig = getStatusConfig(variant.isActive)
  const packagingConfig = getPackagingConfig(variant.packaging)
  const name = getLocalizedName(variant.name, locale)

  const handleClick = () => {
    onSelect?.(variant)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect?.(variant)
    }
  }

  const handlePortalAvailabilityChange = (value: string) => {
    if (value !== 'ecommerce' && value !== 'portal' && value !== 'both') return
    onPortalAvailabilityChange?.(variant, value)
  }

  return (
    <Card
      className={cn(
        'group relative cursor-pointer overflow-hidden border-border/70 bg-gradient-to-b from-card to-muted/20',
        'transition-[box-shadow,border-color,transform] duration-200 hover:-translate-y-0.5 hover:shadow-lg',
        isSelected && 'ring-2 ring-primary shadow-lg'
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary/0 via-primary/80 to-primary/0 opacity-0 transition-opacity group-hover:opacity-100" />

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Box className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold truncate">{name}</CardTitle>
              <p className="mt-0.5 text-xs font-mono text-muted-foreground">{variant.variantCode}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="secondary" className={cn('border-0', statusConfig.bgClass, statusConfig.textClass)}>
              {t(statusConfig.labelKey)}
            </Badge>
            <Badge variant="secondary" className={cn('border-0', packagingConfig.bgClass, packagingConfig.textClass)}>
              {t(packagingConfig.labelKey)}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-muted/50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Scale className="h-3.5 w-3.5" />
                <span>{t('products.table.weight')}</span>
              </div>
              <p className="mt-1 text-sm font-semibold text-foreground">{formatWeight(variant.weight)}</p>
            </div>

            <div className="rounded-lg bg-muted/50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Barcode className="h-3.5 w-3.5" />
                <span>{t('products.table.barcode')}</span>
              </div>
              <p className="mt-1 truncate text-sm font-mono text-foreground">{variant.barcode}</p>
            </div>
          </div>

          {variant.sku && (
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              <span>{t('products.form.sku')}: </span>
              <span className="font-medium text-foreground">
                {getLocalizedName(variant.sku.name, locale)}
              </span>
            </div>
          )}

          <div
            className="rounded-lg border border-border/70 bg-muted/20 p-2"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between px-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t('products.form.portalAvailability')}
              </span>
              {isUpdatingPortalAvailability && (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('common.loading', 'Loading')}
                </span>
              )}
            </div>
            <ToggleGroup
              type="single"
              value={variant.portalAvailability || 'both'}
              onValueChange={handlePortalAvailabilityChange}
              disabled={isUpdatingPortalAvailability}
              variant="outline"
              size="sm"
              className={cn(
                'w-full rounded-xl border-border/70 bg-background p-1 shadow-inner',
                isUpdatingPortalAvailability && 'opacity-80'
              )}
            >
              {PORTAL_AVAILABILITY_OPTIONS.map((option) => (
                <ToggleGroupItem
                  key={option}
                  value={option}
                  className={cn(
                    'h-8 flex-1 rounded-lg px-2 text-[12px] font-semibold capitalize',
                    'text-muted-foreground transition-colors',
                    'data-[state=on]:border-primary/30 data-[state=on]:bg-primary',
                    'data-[state=on]:text-primary-foreground data-[state=on]:shadow-sm'
                  )}
                >
                  {t(PORTAL_AVAILABILITY_CONFIG[option].labelKey)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
